import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import MapView from '@arcgis/core/views/MapView';
import { GoldenLayoutContainerInjectionToken, WidgetHostDirective } from '../../../_directives/widget-host.directive';
import { ComponentContainer } from 'golden-layout';
import { DashboardService } from '../../dashboard/dashboard.service';
import { ConfigService } from '../../../_services/local/config.service';
import { RefreshMapService } from '../../../_services/http/refresh-map.service';
import { EsriHelpers } from '../../../_helpers/EsriHelpers';
import config from '@arcgis/core/config';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import Sublayer from '@arcgis/core/layers/support/Sublayer';
import { MessageService } from '../../../_services/local/message.service';
import { ArxThematicWidget } from '@arxit/esri/widgets/ArxThematic';
import { History } from '../../../_models/History';
import { Store } from '@ngrx/store';
import { MainStoreState } from 'src/app/_store/mainStoreState';

@Component({
  selector: 'app-historique',
  templateUrl: './historique.component.html',
  styleUrls: ['./historique.component.scss'],
})
export class HistoriqueComponent extends WidgetHostDirective implements OnInit, AfterViewInit, OnDestroy {
  @Output() closed = new EventEmitter<{ event: Event; uuid: string }>();
  @Output() openedIneNew = new EventEmitter<{ event: Event; uuid: string }>();
  @Output() resized = new EventEmitter<number>();
  @ViewChild('mapViewNode', { static: true }) private mapViewEl!: ElementRef;

  selectionLoading = false;
  exportLoading = false;

  private readonly portalUrl = this.configService.Config.portalUrl;
  private readonly mapId = this.configService.Config.mapHistoriqueId;
  private onDestroy$ = new Subject<boolean>();
  private mapView!: MapView;
  private historizedLayers: Sublayer[] = [];

  constructor(
    @Inject(GoldenLayoutContainerInjectionToken) container: ComponentContainer,
    elRef: ElementRef,
    renderer: Renderer2,
    viewContainerRef: ViewContainerRef,
    store: Store<MainStoreState>,
    public configService: ConfigService,
    private refreshMapService: RefreshMapService,
    private messageService: MessageService
  ) {
    super(container, elRef.nativeElement, renderer, viewContainerRef, store);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this.calculateHeight(this.container.height);

    this.initializeMap().then(() => {
      this.loading = false;

      const arxThematic = new ArxThematicWidget({
        view: this.mapView,
        shouldApplyGrayscaleWhenUnchecked: true,
        thematics: this.configService.Config.thematicsHisto,
        position: 'start',
        resizable: true,
        collapsible: true,
        open: false,
      });
      this.mapView.ui.add(arxThematic, 'top-left');

      this.refreshMapService.refreshHubConnection();
      this.refreshMapService.refreshed$.subscribe(() => {
        EsriHelpers.refreshView(this.mapView);
      });
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.mapView.destroy();
  }

  async initializeMap(): Promise<void> {
    try {
      config.portalUrl = this.portalUrl;

      this.mapView = await EsriHelpers.GenerateMapAsync(this.mapViewEl.nativeElement, this.mapId, [], { activateIdentify: true });

      this.mapView.map.layers.forEach((layer) => {
        if (layer instanceof MapImageLayer) {
          layer.sublayers?.forEach((subLayer) => {
            subLayer.load().then(() => {
              if (subLayer.fields && subLayer.fields.findIndex((x) => x.name === this.configService.Config.history.fieldDateStart) > -1) {
                subLayer.definitionExpression = '1=0';
                this.historizedLayers.push(subLayer);
              }
            });
          });
        }
      });

      this.mapView.watch('updating', () => {
        this.selectionLoading = this.mapView.updating;
      });
    } catch (error) {
      console.debug('EsriLoader: ', error);
    }
  }

  filter(whereClause: string): void {
    this.historizedLayers.forEach((layer) => {
      layer.definitionExpression = whereClause;
    });
  }

  export(history: History): void {
    console.debug('export', history);

    this.exportLoading = true;

    const filters = new Map<string, string>();
    filters.set('date', history.formatWhereClause(history.consultation.start, history.consultation.end));

    const promises: Array<Promise<string>> = [];
    this.historizedLayers.forEach((layer) => {
      promises.push(EsriHelpers.exportAllGraphicsToCsv(layer.url, filters));
    });

    Promise.all(promises).then((csvs) => {
      const fullCsvs: Array<{
        content: string;
        filenameWithExtension: string;
      }> = [];

      csvs.forEach((csv, i) => {
        if (csv.length) {
          const csvName = `${this.historizedLayers[i].title}.csv`;
          fullCsvs.push({ content: csv, filenameWithExtension: csvName });
        }
      });

      if (fullCsvs.length > 0) {
        EsriHelpers.downloadExport(fullCsvs, `${EsriHelpers.formatDate(new Date(), true, true).replace(' ', '_')}_sagt_historisation.zip`);
      } else {
        this.messageService.info('Aucune donnée à exporter.');
      }

      this.exportLoading = false;
    });
  }
}
